<template>
   <b-container>
      <!-- Search Form -->
      <b-card class="mb-4">
         <b-form @submit.prevent="performSearch">
            <b-row>
               <b-col md="4">
                  <b-form-group
                      label="Batch"
                      label-for="batch-select"
                      class="mb-2"
                      :invalid-feedback="!selectedBatch ? 'Please select a batch' : ''"
                      :state="!!selectedBatch"
                  >
                     <b-form-select
                         id="batch-select"
                         v-model="selectedBatch"
                         :options="batchOptions"
                         :state="!!selectedBatch"
                         required
                         size="sm"
                     >
                        <template #first>
                           <b-form-select-option :value="null">Choose a batch...</b-form-select-option>
                        </template>
                     </b-form-select>
                  </b-form-group>
               </b-col>
               <b-col md="4">
                  <b-form-group
                      label="Article ID"
                      label-for="article-id"
                      class="mb-2"
                  >
                     <b-form-input
                         id="article-id"
                         v-model.number="articleId"
                         type="number"
                         placeholder="Enter article ID (optional)"
                         size="sm"
                     ></b-form-input>
                  </b-form-group>
               </b-col>
               <b-col md="4">
                  <b-form-group
                      label="Description"
                      label-for="search-description"
                      class="mb-2"
                  >
                     <b-form-input
                         id="search-description"
                         v-model="searchDescription"
                         placeholder="Enter a description (optional)"
                         size="sm"
                     ></b-form-input>
                  </b-form-group>
               </b-col>
            </b-row>
            <b-row>
               <b-col md="4">
                  <b-form-group
                      label="Result Limit"
                      label-for="limit"
                      class="mb-2"
                  >
                     <b-form-select
                         id="limit"
                         v-model="limit"
                         :options="[5, 10, 20, 50, 100]"
                         size="sm"
                     ></b-form-select>
                  </b-form-group>
               </b-col>
               <b-col md="4" class="d-flex align-items-end">
                  <b-button
                      type="submit"
                      variant="primary"
                      size="sm"
                      class="ml-auto mb-2"
                      :disabled="!canSearch || isSearching"
                  >
                     <b-spinner small v-if="isSearching"></b-spinner>
                     {{ isSearching ? 'Searching...' : 'Search' }}
                  </b-button>
               </b-col>
            </b-row>
         </b-form>
      </b-card>

      <!-- Search Results -->
      <div v-if="!isSearching && searchResults.length > 0">
         <h3 class="mb-3">Search Results</h3>
         <b-row>
            <b-col
                v-for="result in searchResults"
                :key="result.id"
                class="col-md-4 col-lg-2dot4 mb-2 card-deck"
            >
               <b-card>
                  <template #header>


                     <div class="d-flex justify-content-between align-items-center">
                        <h6 class="mb-0">Score: {{ formatScore(result.similarity) }}</h6>
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            @click="showResultDetails(result)"
                        >
                           Details
                        </b-button>
                     </div>
                     <div>
                        <h6 class="article-name mt-2 text-left">{{ result.articleName }}</h6>
                     </div>
                  </template>
                  <b-img
                      v-if="result.imageUrl"
                      :src="result.imageUrl"
                      fluid
                      thumbnail
                      class="mb-2"
                  ></b-img>
               </b-card>
            </b-col>
         </b-row>
      </div>
      <div v-if="isSearching" class="text-center my-4">
         <b-spinner variant="primary"></b-spinner>
         <p class="mt-2">Searching...</p>
      </div>

      <!-- No Results Message -->
      <b-alert
          v-if="!isSearching && hasSearched && searchResults.length === 0" show
          variant="info"
      >
         No results found matching your criteria.
      </b-alert>
      <!-- Details Modal -->
      <b-modal
          v-model="showDetailsModal"
          :title="'Image Details'"
          size="lg"
          hide-footer
      >
         <template v-if="selectedResult">
            <div class="d-flex mb-3">
               <b-img
                   v-if="selectedResult.imageUrl"
                   :src="selectedResult.imageUrl"
                   fluid
                   thumbnail
                   class="details-image mr-3"
               ></b-img>
               <div>
                  <p class="mb-2"><strong>ID:</strong> {{ selectedResult.articleId }}</p>
                  <p class="mb-2"><strong>Similarity Score:</strong> {{ formatScore(selectedResult.similarity) }}</p>
                  <p class="mb-2"><strong>Description:</strong></p>
                  <p class="description-text">{{ selectedResult.description }}</p>
                  <!-- Add other relevant result fields here -->
               </div>
            </div>
         </template>
      </b-modal>
   </b-container>
</template>

<script>
import ArticleApi from "@shared/_api/article";

export default {
   name: 'ImageSearch',
   data() {
      return {
         selectedBatch: null,
         searchDescription: '',
         limit: 10,
         articleId: null,
         isSearching: false,
         hasSearched: false,
         searchResults: [],
         batches: [],
         isLoadingBatches: false,
         showDetailsModal: false,
         selectedResult: null
      }
   },
   computed: {
      batchOptions() {
         return this.batches
             .filter(batch => batch.statusEmbeddings === 'COMPLETED')
             .map(batch => ({
                value: batch.id,
                text: `${batch.batchLabel} (ID: ${batch.id})`
             }));
      },
      canSearch() {
         // Updated validation to only require batch selection
         return this.selectedBatch && !this.isSearching;
      }
   },
   async created() {
      await this.loadBatches();
   },
   methods: {
      async loadBatches() {
         this.isLoadingBatches = true;
         try {
            const response = await ArticleApi.getBatches();
            this.batches = response.content;
            if (this.batches.length > 0) {
               this.selectedBatch = this.batches[0].id;
            }
         } catch (error) {
            this.$bvToast.toast('Failed to load batches', {
               title: 'Error',
               variant: 'danger'
            });
         } finally {
            this.isLoadingBatches = false;
         }
      },

      async performSearch() {
         if (!this.canSearch) return;

         this.isSearching = true;
         this.searchResults = [];

         try {
            const results = await ArticleApi.searchSimilarImages(
                this.selectedBatch,
                this.searchDescription,
                {
                   limit: this.limit,
                   articleId: this.articleId
                }
            );

            // Set the results first, then set hasSearched
            this.searchResults = results;
            this.hasSearched = true; // Move this after setting results
         } catch (error) {
            this.$bvToast.toast('Search failed', {
               title: 'Error',
               variant: 'danger'
            });
         } finally {
            this.isSearching = false;
         }
      },

      formatScore(score) {
         return (score * 100).toFixed(1) + '%';
      },

      showResultDetails(result) {
         this.selectedResult = result;
         this.showDetailsModal = true;
      }
   }
}
</script>

<style scoped>
.result-details {
   font-size: 0.9rem;
}

.btn-primary {
   transition: all 0.3s ease;
}

.score-badge {
   font-size: 0.9rem;
   font-weight: 500;
}

.card-img-top {
   height: 200px;
   object-fit: contain;
   background-color: #f8f9fa;
}

.details-image {
   max-width: 300px;
   max-height: 300px;
   object-fit: contain;
}

.description-text {
   white-space: pre-wrap;
}

/* Make form more compact */
.form-group {
   margin-bottom: 0.5rem;
}

label {
   margin-bottom: 0.25rem;
}

.article-name {
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
   overflow: hidden;
   text-overflow: ellipsis;
   line-height: 1.2;
   max-height: 2.4em; /* 2 lines × 1.2 line-height */
   margin-bottom: 0;
}

</style>